/**
 * -------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation.  All Rights Reserved.  Licensed under the MIT License.
 * See License in the project root for license information.
 * -------------------------------------------------------------------------------------------
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { customElement, html, property } from 'lit-element';
import { Providers } from '../../Providers';
import { ProviderState } from '../../providers/IProvider';
import '../../styles/fabric-icon-font';
import { prepScopes } from '../../utils/GraphHelpers';
import { getDayOfWeekString, getMonthString } from '../../utils/Utils';
import '../mgt-person/mgt-person';
import { MgtTemplatedComponent } from '../templatedComponent';
import { styles } from './mgt-agenda-css';
import { getEventsPageIterator } from './mgt-agenda.graph';
/**
 * Web Component which represents events in a user or group calendar.
 *
 * @export
 * @class MgtAgenda
 * @extends {MgtTemplatedComponent}
 *
 * @fires eventClick - Fired when user click an event
 *
 * @cssprop --event-box-shadow - {String} Event box shadow color and size
 * @cssprop --event-margin - {String} Event margin
 * @cssprop --event-padding - {String} Event padding
 * @cssprop --event-background - {Color} Event background color
 * @cssprop --event-border - {String} Event border color
 * @cssprop --agenda-header-margin - {String} Agenda header margin size
 * @cssprop --agenda-header-font-size - {Length} Agenda header font size
 * @cssprop --agenda-header-color - {Color} Agenda header color
 * @cssprop --event-time-font-size - {Length} Event time font size
 * @cssprop --event-time-color - {Color} Event time color
 * @cssprop --event-subject-font-size - {Length} Event subject font size
 * @cssprop --event-subject-color - {Color} Event subject color
 * @cssprop --event-location-font-size - {Length} Event location font size
 * @cssprop --event-location-color - {Color} Event location color
 */
let MgtAgenda = class MgtAgenda extends MgtTemplatedComponent {
    constructor() {
        super();
        this._days = 3;
        this.onResize = this.onResize.bind(this);
    }
    /**
     * Array of styles to apply to the element. The styles should be defined
     * using the `css` tag function.
     */
    static get styles() {
        return styles;
    }
    /**
     * stores current date for initial calender selection in events.
     * @type {string}
     */
    get date() {
        return this._date;
    }
    set date(value) {
        if (this._date === value) {
            return;
        }
        this._date = value;
        this.requestStateUpdate(true);
    }
    /**
     * determines if agenda events come from specific group
     * @type {string}
     */
    get groupId() {
        return this._groupId;
    }
    set groupId(value) {
        if (this._groupId === value) {
            return;
        }
        this._groupId = value;
        this.requestStateUpdate(true);
    }
    /**
     * sets number of days until end date, 3 is the default
     * @type {number}
     */
    get days() {
        return this._days;
    }
    set days(value) {
        if (this._days === value) {
            return;
        }
        this._days = value;
        this.requestStateUpdate(true);
    }
    /**
     * allows developer to specify a different graph query that retrieves events
     * @type {string}
     */
    get eventQuery() {
        return this._eventQuery;
    }
    set eventQuery(value) {
        if (this._eventQuery === value) {
            return;
        }
        this._eventQuery = value;
        this.requestStateUpdate(true);
    }
    /**
     * Determines width available if resize is necessary, adds onResize event listener to window
     *
     * @memberof MgtAgenda
     */
    connectedCallback() {
        this._isNarrow = this.offsetWidth < 600;
        super.connectedCallback();
        window.addEventListener('resize', this.onResize);
    }
    /**
     * Removes onResize event listener from window
     *
     * @memberof MgtAgenda
     */
    disconnectedCallback() {
        window.removeEventListener('resize', this.onResize);
        super.disconnectedCallback();
    }
    /**
     * Invoked on each update to perform rendering tasks. This method must return a lit-html TemplateResult.
     * Setting properties inside this method will not trigger the element to update
     *
     * @returns
     * @memberof MgtAgenda
     */
    render() {
        // Loading
        if (!this.events && this.isLoadingState) {
            return this.renderLoading();
        }
        // No data
        if (!this.events || this.events.length === 0) {
            return this.renderNoData();
        }
        // Prep data
        const events = this.showMax && this.showMax > 0 ? this.events.slice(0, this.showMax) : this.events;
        // Default template
        const renderedTemplate = this.renderTemplate('default', { events });
        if (renderedTemplate) {
            return renderedTemplate;
        }
        // Update narrow state
        this._isNarrow = this.offsetWidth < 600;
        // Render list
        return html `
      <div class="agenda${this._isNarrow ? ' narrow' : ''}${this.groupByDay ? ' grouped' : ''}">
        ${this.groupByDay ? this.renderGroups(events) : this.renderEvents(events)}
        ${this.isLoadingState ? this.renderLoading() : html ``}
      </div>
    `;
    }
    /**
     * Request to reload the state.
     * Use reload instead of load to ensure loading events are fired.
     *
     * @protected
     * @memberof MgtBaseComponent
     */
    requestStateUpdate(force) {
        const _super = Object.create(null, {
            requestStateUpdate: { get: () => super.requestStateUpdate }
        });
        return __awaiter(this, void 0, void 0, function* () {
            if (force) {
                this.events = null;
            }
            _super.requestStateUpdate.call(this, force);
        });
    }
    /**
     * Render the loading state
     *
     * @protected
     * @returns
     * @memberof MgtAgenda
     */
    renderLoading() {
        return (this.renderTemplate('loading', null) ||
            html `
        <div class="event">
          <div class="event-time-container">
            <div class="event-time-loading loading-element"></div>
          </div>
          <div class="event-details-container">
            <div class="event-subject-loading loading-element"></div>
            <div class="event-location-container">
              <div class="event-location-icon-loading loading-element"></div>
              <div class="event-location-loading loading-element"></div>
            </div>
            <div class="event-location-container">
              <div class="event-attendee-loading loading-element"></div>
              <div class="event-attendee-loading loading-element"></div>
              <div class="event-attendee-loading loading-element"></div>
            </div>
          </div>
        </div>
      `);
    }
    /**
     * Render the no-data state.
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtAgenda
     */
    renderNoData() {
        return this.renderTemplate('no-data', null) || html ``;
    }
    /**
     * Render an individual Event.
     *
     * @protected
     * @param {MicrosoftGraph.Event} event
     * @returns
     * @memberof MgtAgenda
     */
    renderEvent(event) {
        return html `
      <div class="event">
        <div class="event-time-container">
          <div class="event-time" aria-label="${this.getEventTimeString(event)}">${this.getEventTimeString(event)}</div>
        </div>
        <div class="event-details-container">
          ${this.renderTitle(event)} ${this.renderLocation(event)} ${this.renderAttendees(event)}
        </div>
        <div class="event-other-container">
          ${this.renderOther(event)}
        </div>
      </div>
    `;
        // <div class="event-duration">${this.getEventDuration(event)}</div>
    }
    /**
     * Render the header for a group.
     * Only relevant for grouped Events.
     *
     * @protected
     * @param {Date} date
     * @returns
     * @memberof MgtAgenda
     */
    renderHeader(header) {
        return (this.renderTemplate('header', { header }, 'header-' + header) ||
            html `
        <div class="header" aria-label="${header}">${header}</div>
      `);
    }
    /**
     * Render the title field of an Event
     *
     * @protected
     * @param {MicrosoftGraph.Event} event
     * @returns
     * @memberof MgtAgenda
     */
    renderTitle(event) {
        return html `
      <div class="event-subject">${event.subject}</div>
    `;
    }
    /**
     * Render the location field of an Event
     *
     * @protected
     * @param {MicrosoftGraph.Event} event
     * @returns
     * @memberof MgtAgenda
     */
    renderLocation(event) {
        if (!event.location.displayName) {
            return null;
        }
        return html `
      <div class="event-location-container">
        <svg width="10" height="13" viewBox="0 0 10 13" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M4.99989 6.49989C4.15159 6.49989 3.46143 5.81458 3.46143 4.97224C3.46143 4.12965 4.15159 3.44434 4.99989 3.44434C5.84845 3.44434 6.53835 4.12965 6.53835 4.97224C6.53835 5.81458 5.84845 6.49989 4.99989 6.49989Z"
            stroke="black"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M8.1897 7.57436L5.00029 12L1.80577 7.56765C0.5971 6.01895 0.770299 3.47507 2.17681 2.12383C2.93098 1.39918 3.93367 1 5.00029 1C6.06692 1 7.06961 1.39918 7.82401 2.12383C9.23075 3.47507 9.40372 6.01895 8.1897 7.57436Z"
            stroke="black"
          />
        </svg>
        <div class="event-location" aria-label="${event.location.displayName}">${event.location.displayName}</div>
      </div>
    `;
    }
    /**
     * Render the attendees field of an Event
     *
     * @protected
     * @param {MicrosoftGraph.Event} event
     * @returns
     * @memberof MgtAgenda
     */
    renderAttendees(event) {
        if (!event.attendees.length) {
            return null;
        }
        return html `
      <mgt-people
        class="event-attendees"
        .peopleQueries=${event.attendees.map(attendee => {
            return attendee.emailAddress.address;
        })}
      ></mgt-people>
    `;
    }
    /**
     * Render the event other field of an Event
     *
     * @protected
     * @param {MicrosoftGraph.Event} event
     * @returns
     * @memberof MgtAgenda
     */
    renderOther(event) {
        return this.hasTemplate('event-other')
            ? html `
          ${this.renderTemplate('event-other', { event }, event.id + '-other')}
        `
            : null;
    }
    /**
     * Render the events in groups, each with a header.
     *
     * @protected
     * @param {MicrosoftGraph.Event[]} events
     * @returns {TemplateResult}
     * @memberof MgtAgenda
     */
    renderGroups(events) {
        // Render list, grouped by day
        const grouped = {};
        events.forEach(event => {
            const header = this.getDateHeaderFromDateTimeString(event.start.dateTime);
            grouped[header] = grouped[header] || [];
            grouped[header].push(event);
        });
        return html `
      ${Object.keys(grouped).map(header => html `
            <div class="group">
              ${this.renderHeader(header)} ${this.renderEvents(grouped[header])}
            </div>
          `)}
    `;
    }
    /**
     * Render a list of events.
     *
     * @protected
     * @param {MicrosoftGraph.Event[]} events
     * @returns {TemplateResult}
     * @memberof MgtAgenda
     */
    renderEvents(events) {
        return html `
      <ul class="agenda-list">
        ${events.map(event => html `
              <li @click=${() => this.eventClicked(event)}>
                ${this.renderTemplate('event', { event }, event.id) || this.renderEvent(event)}
              </li>
            `)}
      </ul>
    `;
    }
    /**
     * Load state into the component
     *
     * @protected
     * @returns
     * @memberof MgtAgenda
     */
    loadState() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.events) {
                return;
            }
            const p = Providers.globalProvider;
            if (p && p.state === ProviderState.SignedIn) {
                const graph = p.graph.forComponent(this);
                if (this.eventQuery) {
                    try {
                        const tokens = this.eventQuery.split('|');
                        let scope;
                        let query;
                        if (tokens.length > 1) {
                            query = tokens[0].trim();
                            scope = tokens[1].trim();
                        }
                        else {
                            query = this.eventQuery;
                        }
                        let request = yield graph.api(query);
                        if (scope) {
                            request = request.middlewareOptions(prepScopes(scope));
                        }
                        const results = yield request.get();
                        if (results && results.value) {
                            this.events = results.value;
                        }
                        // tslint:disable-next-line: no-empty
                    }
                    catch (e) { }
                }
                else {
                    const start = this.date ? new Date(this.date) : new Date();
                    start.setHours(0, 0, 0, 0);
                    const end = new Date(start.getTime());
                    end.setDate(start.getDate() + this.days);
                    try {
                        const iterator = yield getEventsPageIterator(graph, start, end, this.groupId);
                        if (iterator && iterator.value) {
                            this.events = iterator.value;
                            while (iterator.hasNext) {
                                yield iterator.next();
                                this.events = iterator.value;
                            }
                        }
                    }
                    catch (error) {
                        // noop - possible error with graph
                    }
                }
            }
        });
    }
    onResize() {
        this._isNarrow = this.offsetWidth < 600;
    }
    eventClicked(event) {
        this.fireCustomEvent('eventClick', { event });
    }
    getEventTimeString(event) {
        if (event.isAllDay) {
            return 'ALL DAY';
        }
        const start = this.prettyPrintTimeFromDateTime(new Date(event.start.dateTime));
        const end = this.prettyPrintTimeFromDateTime(new Date(event.end.dateTime));
        return `${start} - ${end}`;
    }
    prettyPrintTimeFromDateTime(date) {
        date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
        let hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;
        const minutesStr = minutes < 10 ? '0' + minutes : minutes;
        return `${hours}:${minutesStr} ${ampm}`;
    }
    getDateHeaderFromDateTimeString(dateTimeString) {
        const date = new Date(dateTimeString);
        date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
        const dayIndex = date.getDay();
        const monthIndex = date.getMonth();
        const day = date.getDate();
        const year = date.getFullYear();
        return `${getDayOfWeekString(dayIndex)}, ${getMonthString(monthIndex)} ${day}, ${year}`;
    }
    getEventDuration(event) {
        let dtStart = new Date(event.start.dateTime);
        const dtEnd = new Date(event.end.dateTime);
        const dtNow = new Date();
        let result = '';
        if (dtNow > dtStart) {
            dtStart = dtNow;
        }
        const diff = dtEnd.getTime() - dtStart.getTime();
        const durationMinutes = Math.round(diff / 60000);
        if (durationMinutes > 1440 || event.isAllDay) {
            result = Math.ceil(durationMinutes / 1440) + 'd';
        }
        else if (durationMinutes > 60) {
            result = Math.round(durationMinutes / 60) + 'h';
            const leftoverMinutes = durationMinutes % 60;
            if (leftoverMinutes) {
                result += leftoverMinutes + 'm';
            }
        }
        else {
            result = durationMinutes + 'm';
        }
        return result;
    }
};
__decorate([
    property({
        attribute: 'date',
        type: String
    })
], MgtAgenda.prototype, "date", null);
__decorate([
    property({
        attribute: 'group-id',
        type: String
    })
], MgtAgenda.prototype, "groupId", null);
__decorate([
    property({
        attribute: 'days',
        type: Number
    })
], MgtAgenda.prototype, "days", null);
__decorate([
    property({
        attribute: 'event-query',
        type: String
    })
], MgtAgenda.prototype, "eventQuery", null);
__decorate([
    property({
        attribute: 'events'
    })
], MgtAgenda.prototype, "events", void 0);
__decorate([
    property({
        attribute: 'show-max',
        type: Number
    })
], MgtAgenda.prototype, "showMax", void 0);
__decorate([
    property({
        attribute: 'group-by-day',
        type: Boolean
    })
], MgtAgenda.prototype, "groupByDay", void 0);
__decorate([
    property({ attribute: false })
], MgtAgenda.prototype, "_isNarrow", void 0);
MgtAgenda = __decorate([
    customElement('mgt-agenda')
], MgtAgenda);
export { MgtAgenda };
//# sourceMappingURL=mgt-agenda.js.map